import { useTranslation } from "react-i18next";
import { LogOut, User as UserIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_BADGE_CLASSES = {
  admin: "bg-slate-900 text-slate-100 hover:bg-slate-900",
  accountant: "bg-slate-200 text-slate-800 hover:bg-slate-200",
  user: "bg-slate-100 text-slate-600 hover:bg-slate-100",
};

function initialsOf(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("") || "U";
}

export function Topbar() {
  const { t } = useTranslation();
  const { user, company, logout } = useAuth();

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-6 backdrop-blur"
    >
      <div className="flex min-w-0 items-center gap-3">
        {company?.logo_url && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`}
            alt="logo"
            className="h-8 w-8 rounded-md border border-slate-200 bg-white object-contain"
            data-testid="topbar-logo"
          />
        )}
        <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
          {t("topbar.company")}
        </div>
        <div className="h-4 w-px bg-slate-200" />
        <div
          className="truncate text-sm font-medium text-slate-900"
          data-testid="topbar-company-name"
        >
          {company?.name || "—"}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <LanguageSwitcher testId="topbar-lang-switcher" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="gap-3 rounded-full ps-1 pe-3"
              data-testid="topbar-user-menu"
              aria-label={t("topbar.openMenu")}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-slate-900 text-white text-xs font-semibold">
                  {initialsOf(user?.name)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline text-sm text-slate-800">
                {user?.name}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="space-y-2 py-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">
                {t("topbar.signedInAs")}
              </div>
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="truncate text-xs text-slate-500">{user?.email}</div>
              {user?.role && (
                <Badge
                  className={`${ROLE_BADGE_CLASSES[user.role] || ROLE_BADGE_CLASSES.user} mt-1`}
                  data-testid="topbar-user-role"
                >
                  {t(`roles.${user.role}`)}
                </Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled className="gap-2">
              <UserIcon className="h-4 w-4" />
              {user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              data-testid="topbar-logout"
              className="gap-2 text-red-600 focus:text-red-700"
            >
              <LogOut className="h-4 w-4" />
              {t("topbar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export { ROLE_BADGE_CLASSES };
